import posthog from "posthog-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toastUtils } from "../../Utils/helper";
import CardQuestLocation from "../../components/common/Card/CardQuestLocation";
import TermsOfUseModal from "../../components/common/Drawer/TermsOfUseModal";
import Search from "../../components/common/Field/Search";
import LoadingCommon from "../../components/common/LoadingStatus/LoadingCommon";
import CommonModal from "../../components/common/Modal/ModalCommon";
import TipsQuest from "../../components/common/Tips/Tips";
import { useQuestStatusUpdater } from "../../hooks/useQuestStatusUpdater";
import images from "../../images";
import {
  useGetLocationByLatLongQuery,
  useGetUserInfoQuery,
  useLazyGetFavoriteLocationsQuery,
  useLazyGetOngoingQuestListPerLocationQuery,
  useLazyGetQuestListPerLocationMainQuery,
  usePostAcceptTermsOfUseMutation,
  usePostFavoriteLocationMutation,
} from "../../services/apiSlice";
import { getSmartLocation } from "../../Utils/helper";
import UnderReview from "../UnderReview/UnderReview";
import QuestNearYou from "./QuestNearYou";
import FeaturedBrand from "./FeaturedBrand";
import DraggableContainer from "../../components/common/DraggableContainer/DraggableContainer";

const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isFirstLogin = localStorage.getItem("isFirstLogin");

  const [location, setLocation] = useState({
    latitude: null,
    longitude: null,
    error: null,
    loading: true,
    accuracy: null,
    source: null,
    city: null,
    country: null,
    district: null,
  });

  const [questData, setQuestData] = useState(null);
  const [questListData, setQuestListData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFavoriteLocations, setShowFavoriteLocations] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [, setIsSearchClearing] = useState(false);
  const [isModalOpenTerms, setIsModalOpenTerms] = useState(false);
  const [questLimit] = useState(10);
  const [isLoadingMore] = useState(false);
  const [isScrollEnd, setIsScrollEnd] = useState(false);

  const searchTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const validateAcceptTermsOfUseRef = useRef();
  const scrollContainerRef = useRef(null);

  const {
    data: userInfo,
    isLoading: isUserInfoLoading,
    refetch: refetchUserInfo,
  } = useGetUserInfoQuery();

  const isAccountUnderReview = userInfo?.data?.status;
  const [
    triggerQuestList,
    { data: questResponse, isLoading: isLoadingQuestList },
  ] = useLazyGetQuestListPerLocationMainQuery();
  const [
    triggerFavoriteLocations,
    { data: favoriteLocationsResponse, isLoading: isLoadingFavoriteLocations },
  ] = useLazyGetFavoriteLocationsQuery();
  const [
    triggerOngoingQuests,
    {
      data: ongoingQuestResponse,
      error: ongoingQuestError,
      isLoading: isLoadingOngoing,
    },
  ] = useLazyGetOngoingQuestListPerLocationQuery();

  const [favorites, { isLoading: isFavoriteLoading }] =
    usePostFavoriteLocationMutation();
  const { data: locationData, isLoading: isLocationLoading } =
    useGetLocationByLatLongQuery(
      location.latitude && location.longitude
        ? { latitude: location.latitude, longitude: location.longitude }
        : undefined,
      { skip: !location.latitude || !location.longitude },
    );

  const [postAcceptTermsOfUse, { isLoading: isAccepting }] =
    usePostAcceptTermsOfUseMutation();
  const [searchParams, setSearchParams] = useSearchParams();

  const performFetch = useCallback(
    (searchTerm = "", limit = questLimit) => {
      triggerOngoingQuests({
        page: 1,
        limit: limit,
        search: searchTerm.trim(),
      });
      if (location.latitude && location.longitude) {
        triggerQuestList({
          latitude: location.latitude,
          longitude: location.longitude,
          limit: limit,
          search: searchTerm.trim(),
        });
      }
      triggerFavoriteLocations({
        page: 1,
        limit: limit,
        search: searchTerm.trim(),
      });
    },
    [
      triggerOngoingQuests,
      triggerQuestList,
      triggerFavoriteLocations,
      location.latitude,
      location.longitude,
      questLimit,
    ],
  );

  // const handleQuestSaved = useCallback(
  //   (questId, status) => {
  //     performFetch(searchQuery, questLimit);
  //   },
  //   [performFetch, searchQuery, questLimit]
  // );
  const memoizedRefetch = useCallback(() => {
    performFetch(searchQuery, questLimit);
  }, [performFetch, searchQuery, questLimit]);
  useQuestStatusUpdater(questListData, memoizedRefetch);

  useEffect(() => {
    if (isUserInfoLoading) return;
    if (
      userInfo?.data?.accept_terms_of_use !== true &&
      validateAcceptTermsOfUseRef.current
    ) {
      validateAcceptTermsOfUseRef.current.openModal();
    } else if (
      userInfo?.data?.accept_terms_of_use === true &&
      validateAcceptTermsOfUseRef.current
    ) {
      validateAcceptTermsOfUseRef.current.closeModal();
    }
  }, [userInfo, isUserInfoLoading]);
  useEffect(() => {
    if (userInfo?.data?.id) {
      const posthogStorageKey = `ph_${process.env.REACT_APP_POSTHOG_KEY}_posthog`;
      localStorage.removeItem(posthogStorageKey);
      posthog.identify(userInfo.data.id.toString());
      posthog.capture("$identify", { $identified: true });
      posthog.capture("$pageview", { path: window.location.pathname });
    }
  }, [userInfo]);
  useEffect(() => {
    refetchUserInfo();
  }, [refetchUserInfo]);
  useEffect(() => {
    const searchParam = searchParams.get("search");
    if (searchParam) setSearchQuery(searchParam);
    setIsInitialized(true);
  }, [searchParams]);
  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const loc = await getSmartLocation({
          gpsTimeout: 3000,
          fallbackLocation: { latitude: 1.3521, longitude: 103.8198 },
        });
        if (isMountedRef.current)
          setLocation((prev) => ({ ...prev, ...loc, loading: false }));
      } catch (error) {
        if (isMountedRef.current)
          setLocation((prev) => ({
            ...prev,
            loading: false,
            error: error.message,
          }));
      }
    };
    fetchLocation();
  }, []);
  useEffect(() => {
    if (locationData && !isLocationLoading && isMountedRef.current)
      setLocation((prev) => ({
        ...prev,
        city: locationData.data.city || "",
        country: locationData.data.country || "",
        district: locationData.data.district || "",
      }));
  }, [locationData, isLocationLoading]);
  useEffect(() => {
    if (
      location.latitude &&
      location.longitude &&
      !location.loading &&
      isInitialized &&
      !initialFetchDone
    ) {
      performFetch(searchQuery, questLimit);
      setInitialFetchDone(true);
    }
  }, [
    location.latitude,
    location.longitude,
    location.loading,
    isInitialized,
    initialFetchDone,
    performFetch,
    searchQuery,
    questLimit,
  ]);
  useEffect(() => {
    if (!isInitialized || !initialFetchDone) return;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        performFetch(searchQuery, questLimit);
        setIsSearchClearing(false);
      }
    }, 500);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery, isInitialized, initialFetchDone, performFetch, questLimit]);
  useEffect(() => {
    if (ongoingQuestResponse) {
      setQuestListData(ongoingQuestResponse);
      setIsSearchClearing(false);
    }
  }, [ongoingQuestResponse]);
  useEffect(() => {
    if (favoriteLocationsResponse) setIsSearchClearing(false);
  }, [favoriteLocationsResponse]);
  useEffect(() => {
    if (questResponse) setQuestData(questResponse);
  }, [questResponse]);
  useEffect(() => {
    if (ongoingQuestError)
      console.error("❌ Failed to load ongoing quests:", ongoingQuestError);
  }, [ongoingQuestError]);

  const handleSearchChange = useCallback(
    (query) => {
      setSearchQuery(query);
      if (query.trim()) {
        setSearchParams({ search: query });
        performFetch(query.trim(), questLimit);
      } else {
        setIsSearchClearing(true);
        setSearchParams({});
        performFetch("", questLimit);
      }
    },
    [setSearchParams, performFetch, questLimit],
  );
  const handleFavoriteToggle = useCallback(() => {
    setShowFavoriteLocations((prev) => !prev);
  }, []);
  const handleScroll = (e) => {
    const { scrollLeft, scrollWidth, clientWidth } = e.target;
    if (scrollWidth - (scrollLeft + clientWidth) < 20) {
      setIsScrollEnd(true);
    } else {
      setIsScrollEnd(false);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const { scrollWidth, clientWidth, scrollLeft } = container;

      if (
        scrollWidth <= clientWidth ||
        scrollWidth - (scrollLeft + clientWidth) < 20
      ) {
        setIsScrollEnd(true);
      } else {
        setIsScrollEnd(false);
      }
    }
  }, [questData, searchQuery, isInitialized]);
  const scrollToEnd = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      container.scrollTo({
        left: container.scrollWidth,
        behavior: "smooth",
      });
    }
  };
  const handleFavoriteLocation = async (locationId) => {
    try {
      await favorites({ location_id: locationId }).unwrap();
      performFetch(searchQuery, questLimit);
    } catch (error) {
      console.error("Failed to favorite location:", error);
    }
  };
  const handleAcceptTermsOfUse = async () => {
    try {
      const response = await postAcceptTermsOfUse().unwrap();
      validateAcceptTermsOfUseRef.current.closeModal();
      if (response.status === 200) {
        await refetchUserInfo();
        toastUtils.success(t("toast.success.termsOfUseAcceptedSuccessfully"));
      }
    } catch (error) {
      console.error("Failed to accept Terms of Use:", error);
      toastUtils.error(t("toast.error.failedToAcceptTermsOfUse"));
    }
  };

  const hasQuests = (questData?.data || []).length > 0;
  const isQuestDataLoading =
    isLoadingQuestList || isLocationLoading || !initialFetchDone;
  const shouldShowEmptyState = !isQuestDataLoading && !hasQuests;

  return (
    <>
      {isAccountUnderReview === "under-reviewing" ? (
        <UnderReview />
      ) : (
        <>
          <LoadingCommon
            statusLoading={
              location.loading ||
              !initialFetchDone ||
              isFavoriteLoading ||
              isLoadingOngoing ||
              isLoadingFavoriteLocations ||
              isLoadingQuestList ||
              isLocationLoading ||
              isUserInfoLoading ||
              isAccepting ||
              isLoadingMore
            }
          />
          <div className="section-content py-[30px] flex flex-col w-full gap-6 items-center pb-8">
            {isFirstLogin === "true" && (
              <TipsQuest isFirstLogin={isFirstLogin} />
            )}

            <div className="w-full max-w-[368px]">
              <Search
                className="w-full"
                placeholder={t("base.search")}
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                onFavoriteToggle={handleFavoriteToggle}
                isFavorited={showFavoriteLocations}
                showFavorites
              />
            </div>

            {questListData?.data?.length > 0 && (
              <div className="w-full max-w-[368px]">
                <div className="flex flex-col gap-3">
                  <h5 className="text-primary font-[Figtree] text-xl font-bold not-italic leading-6">
                    Recent Quests
                  </h5>
                </div>

                <DraggableContainer className="flex items-start gap-[18px] w-full overflow-x-auto pb-2 scrollbar-hide mt-3">
                  {(questListData?.data || []).map((item) => (
                    <CardQuestLocation
                      key={item.location.id}
                      businessName={
                        item.quests[0]?.quest_business?.business
                          ?.company_name || ""
                      }
                      locationId={item.location?.id}
                      isFavorited={item.quests[0]?.is_favorite}
                      onFavoriteLocation={handleFavoriteLocation}
                      businessAvatar={
                        item.quests[0]?.quest_business?.business?.avatar_data
                          ?.file_path || ""
                      }
                      thumnail={item.quests[0]?.quest_businesses}
                      address={item.location?.name}
                      handleNavigateToDetails={() =>
                        navigate(`/quest/location/${item.location?.id}`)
                      }
                    />
                  ))}
                </DraggableContainer>
              </div>
            )}

            {/* 5. QUEST NEAR YOU */}

            {hasQuests ? (
              <div className="w-full max-w-[368px]">
                <div className="flex justify-between items-center">
                  <h5 className="text-primary font-[Figtree] text-xl font-bold not-italic leading-6">
                    All Quests
                  </h5>
                  <QuestNearYou
                    city={location.city}
                    country={location.country}
                    district={location.district}
                  />
                </div>
                <div className="relative w-full min-h-[140px]">
                  <DraggableContainer
                    innerRef={scrollContainerRef}
                    onScroll={handleScroll}
                    className="flex items-start gap-[18px] w-full overflow-x-auto pb-2 scrollbar-hide mt-6"
                  >
                    {(questData?.data || [])
                      .filter((quest) => quest.business)
                      .slice(0, 10)
                      .map((quest, index) => (
                        <CardQuestLocation
                          key={index}
                          businessName={quest.business?.company_name || ""}
                          locationId={quest.location?.id}
                          isFavorited={quest.is_favorite}
                          onFavoriteLocation={handleFavoriteLocation}
                          businessAvatar={
                            quest.business?.avatar_data?.file_path || ""
                          }
                          thumnail={quest.quest_businesses}
                          address={quest.location?.name}
                          handleNavigateToDetails={() =>
                            navigate(`/quest/location/${quest.location?.id}`)
                          }
                        />
                      ))}
                    <div className="h-[105px] w-16 flex flex-col justify-end items-center flex-shrink-0">
                      {(questData?.data || []).filter((quest) => quest.business)
                        .length > 0 && (
                        <button
                          onClick={() => navigate("/quest/all")}
                          className="size-10"
                          aria-label="See all quests"
                        >
                          <span className="icon icon-arrow-right text-primary" />
                        </button>
                      )}
                    </div>

                    <div
                      className={`absolute right-0 top-0 h-full w-[100px] z-10 flex justify-end 
                        transition-opacity duration-500 ease-in-out
                        ${
                          isScrollEnd
                            ? "opacity-0 pointer-events-none"
                            : "opacity-100"
                        }
                      `}
                      style={{
                        background:
                          "linear-gradient(to left, #fff 20%, rgba(255,255,255,0) 100%)",
                      }}
                    >
                      <div
                        onClick={isScrollEnd ? undefined : scrollToEnd}
                        className={`h-[105px] w-16 flex flex-col justify-end items-center 
                          ${
                            isScrollEnd
                              ? "pointer-events-none"
                              : "pointer-events-auto cursor-pointer"
                          }
                        `}
                      >
                        <div className="size-10 flex items-center justify-center ">
                          <span className="icon icon-arrow-right text-primary" />
                        </div>
                      </div>
                    </div>
                  </DraggableContainer>
                </div>
              </div>
            ) : shouldShowEmptyState ? (
              <div className="w-full max-w-[368px]">
                <div className="flex flex-col justify-center items-center text-center px-4 mt-4 gap-6 w-full">
                  <div className="flex justify-center items-center">
                    <img
                      src={images.ReviewReceiptImage}
                      alt={"No quests available"}
                      className="w-[170px]"
                    />
                  </div>
                  <div className="flex flex-col justify-center font-medium text-2xl w-full text-center text-primary">
                    <p>{t("home.wereSorry")}</p>
                  </div>
                  <div className="text-dark-gray text-lg">
                    <p>{t("home.thereAreNoQuests")}</p>
                  </div>
                  <div className="text-mid-gray text-sm">
                    <p>{t("home.pleaseTryAgainSoon")}</p>
                  </div>
                </div>
              </div>
            ) : null}

            <FeaturedBrand />
          </div>
        </>
      )}

      <CommonModal
        ref={validateAcceptTermsOfUseRef}
        classNameText="text-[#201D1B] font-medium text-lg"
        canClickOverLay={false}
        showCloseIcon={false}
      >
        <div className="py-[54px]">
          <h1 className="font-primary font-bold text-2xl sm:text-2xl md:text-[2rem] tracking-[-0.02em] text-center text-[#253FF6]">
            {t("home.weHaveUpdated")}
          </h1>
          <h1
            onClick={() => setIsModalOpenTerms(true)}
            className="font-primary font-bold text-2xl sm:text-2xl md:text-[2rem] tracking-[-0.02em] text-center underline decoration-[2px] text-[#253FF6] mb-4"
          >
            {t("home.termsOfUse")}
          </h1>
          <div className="font-figtree font-medium text-base sm:text-lg md:text-xl text-center text-[#253FF6] px-6 ">
            {t("home.pleaseAccept")}
          </div>
          <div className="w-full flex justify-center">
            <button
              onClick={handleAcceptTermsOfUse}
              className="gap-2.5 self-stretch px-5 max-w-[368px] mt-5 w-full text-base sm:text-lg md:text-xl font-medium tracking-tight text-center bg-[#253FF6] text-white rounded-md border border-[#253FF6] min-h-[50px]"
              disabled={isAccepting}
            >
              {t("base.accept")}
            </button>
          </div>
        </div>
      </CommonModal>

      <TermsOfUseModal
        isOpen={isModalOpenTerms}
        onOpenChange={setIsModalOpenTerms}
        onClose={() => setIsModalOpenTerms(false)}
      />
    </>
  );
};

export default Home;
